import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface Tier {
  name: string;
  setup: string;
  monthly: string;
  note?: string;
  badge?: string;
  feature?: string;
}
interface Group {
  title: string;
  tiers: Tier[];
}
const groups: Group[] = [{
  title: 'AUTOMATIZACIÓN',
  tiers: [{
    name: 'START',
    setup: '$1.200.000',
    monthly: '$300.000/mes',
    feature: 'IA por WhatsApp + CRM básico'
  }, {
    name: 'GROWTH',
    setup: '$2.500.000',
    monthly: '$650.000/mes',
    badge: 'MÁS ELEGIDO',
    feature: 'Calificación avanzada + agenda + seguimiento'
  }, {
    name: 'PRO',
    setup: '$4.500.000',
    monthly: '$1.200.000/mes',
    feature: 'Multi-canal + automatizaciones avanzadas'
  }]
}, {
  title: 'SISTEMA COMPLETO',
  tiers: [{
    name: 'START',
    setup: '$4.500.000',
    monthly: '$500.000/mes',
    feature: 'Presencia digital + automatización base'
  }, {
    name: 'GROWTH',
    setup: '$6.000.000',
    monthly: '$900.000/mes',
    feature: 'Presencia digital + automatización completa'
  }, {
    name: 'PRO',
    setup: '$8.000.000',
    monthly: '$1.450.000/mes',
    feature: 'Sistema completo + integraciones avanzadas'
  }]
}, {
  title: 'VOICE',
  tiers: [{
    name: 'CORE',
    setup: '$2.500.000',
    monthly: '$1.500.000/mes',
    note: '750 minutos incluidos'
  }, {
    name: 'GROWTH',
    setup: '$4.000.000',
    monthly: '$2.800.000/mes',
    note: '1.500 minutos incluidos'
  }, {
    name: 'SCALE',
    setup: '$6.000.000',
    monthly: '$4.800.000/mes',
    note: '3.000 minutos incluidos'
  }, {
    name: 'ENTERPRISE',
    setup: 'Cotización personalizada',
    monthly: ''
  }]
}];
interface PricingDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PricingDetailModal({
  isOpen,
  onClose
}: PricingDetailModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-139" data-mp="1vu1w782fs940">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[200] bg-carbon/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" role="presentation" onClick={onClose} data-id="element-140" data-mp="1vsqurewgrdg2">
          <motion.div initial={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} role="dialog" aria-modal="true" aria-labelledby="pricing-detail-title" className="relative bg-arena rounded-[12px] max-w-[760px] w-full max-h-[85vh] overflow-y-auto p-8 md:p-10" onClick={e => e.stopPropagation()} data-id="element-141" data-mp="2r35jznawu4ff">
            <button onClick={onClose} aria-label="Cerrar" className="absolute top-6 right-6 text-carbon/50 hover:text-carbon transition-colors duration-150" data-id="element-142" data-mp="3erot251sal8k">
              <X size={20} data-id="element-143" data-mp="3oqdz90kbih8z" />
            </button>

            <h3 id="pricing-detail-title" className="font-display text-[24px] md:text-[28px] font-bold text-carbon mb-1.5" data-id="element-144" data-mp="1pe1rc76arnrq">
              Configuraciones detalladas
            </h3>
            <p className="font-body text-[14px] text-carbon/55 mb-8" data-id="element-145" data-mp="3kt769k5o6ny3">
              Niveles de implementación por sistema. Elegimos el nivel
              correcto contigo según el alcance real de tu proceso.
            </p>

            <div className="space-y-8" data-id="element-146" data-mp="3tu523f5yfp5l">
              {groups.map(group => <div key={group.title} data-id="element-147" data-mp="3pu4q016bvq1p">
                  <span className="block font-body text-[11px] uppercase tracking-[0.12em] text-salvia mb-3" data-id="element-148" data-mp="3pqyw88mr7zzn">
                    {group.title}
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-id="element-149" data-mp="2x5p896vtatmo">
                    {group.tiers.map(tier => <div key={tier.name} className="relative bg-arena-dark border border-carbon/10 rounded-[12px] p-4" data-id="element-150" data-mp="3axnaqyv0pr9v">
                        {tier.badge && <span className="absolute -top-2.5 left-3 bg-azul text-arena font-body text-[9px] font-semibold uppercase tracking-[0.08em] px-2 py-0.5 rounded-full" data-id="element-151" data-mp="2eepr2ruk6o3y">
                            {tier.badge}
                          </span>}
                        <span className="block font-display text-[13px] font-bold text-carbon mb-2" data-id="element-152" data-mp="1uc20hxddvl6l">
                          {tier.name}
                        </span>
                        <span className="block font-body text-[13px] text-carbon/70" data-id="element-153" data-mp="1z8sf0g9ji8pw">
                          {tier.setup}
                          {tier.monthly && ' implementación'}
                        </span>
                        {tier.monthly && <span className="block font-body text-[13px] font-medium text-carbon mt-0.5" data-id="element-154" data-mp="2d19qgzgf4kez">
                            {tier.monthly}
                          </span>}
                        {tier.note && <span className="block font-body text-[11px] text-carbon/45 mt-1.5" data-id="element-155" data-mp="11ph76rdckz0e">
                            {tier.note}
                          </span>}
                        {tier.feature && <span className="block font-body text-[11px] text-carbon/45 mt-1.5 leading-[1.4]" data-id="element-156" data-mp="349jla6wleaqn">
                            {tier.feature}
                          </span>}
                      </div>)}
                  </div>
                </div>)}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}